// apiAuth.js
import axios from "axios";

const api = axios.create({
  baseURL: process.env.REACT_APP_BASE_API_URL1,
  headers: {
    "Content-Type": "application/json",
  },
  withCredentials: true,
});

// Flag to prevent multiple refresh calls at once
let isRefreshing = false;
// Queue of failed requests waiting for token refresh
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// Request interceptor — attach access token
api.interceptors.request.use(
  (config) => {
    const token = JSON.parse(localStorage.getItem("token"));
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor — handle 401 and refresh token
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const originalRequest = error.config;

    // If the failed request is the refresh-token call itself, don't retry
    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !originalRequest.url?.includes("/auth/refresh-token")
    ) {
      if (isRefreshing) {
        // Another refresh is in progress — queue this request
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return api(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const refreshToken = JSON.parse(localStorage.getItem("refreshToken"));

      if (!refreshToken) {
        // No refresh token available — logout
        isRefreshing = false;
        localStorage.removeItem("token");
        localStorage.removeItem("refreshToken");
        localStorage.removeItem("user");
        window.location.href = "/";
        return Promise.reject(error);
      }

      return new Promise((resolve, reject) => {
        axios
          .post(
            `${process.env.REACT_APP_BASE_API_URL1}/auth/refresh-token`,
            { refreshToken }
          )
          .then(({ data }) => {
            const newAccessToken = data?.data?.accessToken || data?.accessToken;
            const newRefreshToken =
              data?.data?.refreshToken || data?.refreshToken;

            localStorage.setItem("token", JSON.stringify(newAccessToken));
            if (newRefreshToken) {
              localStorage.setItem(
                "refreshToken",
                JSON.stringify(newRefreshToken)
              );
            }

            api.defaults.headers.common.Authorization = `Bearer ${newAccessToken}`;
            originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;

            processQueue(null, newAccessToken);
            resolve(api(originalRequest));
          })
          .catch((err) => {
            processQueue(err, null);
            // Refresh failed — logout
            localStorage.removeItem("token");
            localStorage.removeItem("refreshToken");
            localStorage.removeItem("user");
            window.location.href = "/";
            reject(err);
          })
          .finally(() => {
            isRefreshing = false;
          });
      });
    }

    return Promise.reject(error);
  }
);

export default api;
